import { useDropzone } from "react-dropzone";

interface AlertConfig {
  open: boolean;
  type: "loading" | "success" | "error" | "confirm";
  message: string;
  title?: string;
  onConfirm?: () => Promise<void>;
}

interface DropzoneConfig {
  onFileSelect: (files: File[]) => void;
  setFileNameError: (error: string) => void;
  setAlertConfig: (config: AlertConfig) => void;
  disabled?: boolean;
  maxSize?: number;
}

export const useFileDropzone = ({
  onFileSelect,
  setFileNameError,
  setAlertConfig,
  disabled = false,
  maxSize = 10 * 1024 * 1024
}: DropzoneConfig) => {

  const dropzoneConfig = {
    onDrop: onFileSelect,
    accept: {
      "application/pdf": [".pdf"],
      "application/msword": [".doc"],
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document": [".docx"],
      "text/plain": [".txt"],
    },
    disabled,
    maxSize,
    onDropRejected: (rejectedFiles: any[]) => {
      const sizeErrors = rejectedFiles.filter((file) =>
        file.errors.some((e: any) => e.code === "file-too-large")
      );
      const typeErrors = rejectedFiles.filter((file) =>
        file.errors.some((e: any) => e.code === "file-invalid-type")
      );
      const nameErrors = rejectedFiles.filter(
        (file) => file.file.name.length > 150
      );

      let message = "";

      if (nameErrors.length > 0) {
        const fileNames = nameErrors.map((f) => f.file.name).join(", ");
        message += `Los siguientes archivos exceden el límite de 150 caracteres: ${fileNames}.\n`;
        setFileNameError("El nombre del archivo no puede exceder los 150 caracteres.");
      }

      if (sizeErrors.length > 0) {
        const fileNames = sizeErrors.map((f) => f.file.name).join(", ");
        message +=
          sizeErrors.length === 1
            ? `El archivo "${fileNames}" excede el tamaño máximo de 10MB.\n`
            : `Los siguientes archivos exceden el tamaño máximo de 10MB: ${fileNames}.\n`;
      }

      if (typeErrors.length > 0) {
        const fileNames = typeErrors.map((f) => f.file.name).join(", ");
        message +=
          typeErrors.length === 1
            ? `El archivo "${fileNames}" no tiene un formato permitido. Solo se permiten PDF, Word y TXT.`
            : `Los siguientes archivos no tienen un formato permitido: ${fileNames}. Solo se permiten PDF, Word y TXT.`;
      }

      if (message) {
        setAlertConfig({
          open: true,
          type: "error",
          title: "Error en los archivos seleccionados",
          message: message.trim(),
        });
      }
    },
  };

  return useDropzone(dropzoneConfig);
};

export const useNewCollectionDropzone = ({
  onFileSelect,
  setAlertConfig,
  maxSize = 10 * 1024 * 1024
}: {
  onFileSelect: (files: File[]) => void;
  setAlertConfig: (config: AlertConfig) => void;
  maxSize?: number;
}) => {

  const dropzoneConfig = {
    onDrop: onFileSelect,
    accept: {
      "application/pdf": [".pdf"],
      "application/msword": [".doc"],
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document": [".docx"],
      "text/plain": [".txt"],
    },
    maxSize,
    onDropRejected: (rejectedFiles: any[]) => {
      const sizeErrors = rejectedFiles.filter((file) =>
        file.errors.some((e: any) => e.code === "file-too-large")
      );
      const typeErrors = rejectedFiles.filter((file) =>
        file.errors.some((e: any) => e.code === "file-invalid-type")
      );

      if (sizeErrors.length > 0) {
        const fileNames = sizeErrors.map((f) => f.file.name).join(", ");
        const message =
          sizeErrors.length === 1
            ? `El archivo "${fileNames}" excede el tamaño máximo de 10MB`
            : `Los siguientes archivos exceden el tamaño máximo de 10MB: ${fileNames}`;

        setAlertConfig({
          open: true,
          type: "error",
          title: "Archivos demasiado grandes",
          message: message,
        });
      }

      if (typeErrors.length > 0 && sizeErrors.length === 0) {
        const fileNames = typeErrors.map((f) => f.file.name).join(", ");
        const message =
          typeErrors.length === 1
            ? `El archivo "${fileNames}" no tiene un formato permitido. Solo se permiten PDF, Word y TXT.`
            : `Los siguientes archivos no tienen un formato permitido: ${fileNames}. Solo se permiten PDF, Word y TXT.`;

        setAlertConfig({
          open: true,
          type: "error",
          title: "Formato no permitido",
          message: message,
        });
      }
    },
  };

  return useDropzone(dropzoneConfig);
};

export const getDropzoneStyles = (isDragActive: boolean, isDarkMode: boolean) => {
  const baseStyles = `border-dashed border-2 p-4 rounded-lg cursor-pointer transition-colors`;
  const borderColor = isDarkMode ? "border-gray-600" : "border-gray-300";
  const backgroundColor = isDarkMode ? "#27272a" : "#f9fafb";
  
  const activeStyles = isDragActive 
    ? "border-green-400 bg-green-50 dark:bg-green-900/20" 
    : "";

  return `${baseStyles} ${borderColor} ${activeStyles}`;
};

export const getDropzoneText = (isDragActive: boolean, isDarkMode: boolean) => {
  const textColor = isDarkMode ? "text-gray-400" : "text-gray-600";
  
  if (isDragActive) {
    return "text-green-400 text-center";
  }
  
  return `text-center ${textColor}`;
};